import React, {useEffect, useState} from "react";
import {ceremony} from "./FirstSFGData";

interface props {
    onNotify: (message: string) => void
}

const pad = (n: number) => n.toString().padStart(2, '0')

const toICSDate = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

const downloadCalendarInvite = () => {
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//HITL Engineering//1SFG Concept//EN',
        'BEGIN:VEVENT',
        `UID:1sfg-cor-${toICSDate(ceremony.start)}@1stgroup.pages.dev`,
        `DTSTAMP:${toICSDate(new Date().toISOString())}`,
        `DTSTART:${toICSDate(ceremony.start)}`,
        `DTEND:${toICSDate(ceremony.end)}`,
        `SUMMARY:${ceremony.unit} ${ceremony.title}`,
        `LOCATION:${ceremony.location.replace(/,/g, '\\,')}`,
        `DESCRIPTION:${ceremony.outgoing} to ${ceremony.incoming}. Please be seated by 0945.`,
        'END:VEVENT',
        'END:VCALENDAR',
    ]
    const blob = new Blob([lines.join('\r\n')], {type: 'text/calendar'})
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = '1SFG-Change-of-Responsibility.ics'
    link.click()
    URL.revokeObjectURL(link.href)
}

const useCountdown = (target: string) => {
    const [remaining, setRemaining] = useState(() => new Date(target).getTime() - Date.now())
    useEffect(() => {
        const timer = window.setInterval(() => setRemaining(new Date(target).getTime() - Date.now()), 1000)
        return () => window.clearInterval(timer)
    }, [target])
    const clamped = Math.max(0, remaining)
    return {
        days: Math.floor(clamped / 86400000),
        hours: Math.floor(clamped / 3600000) % 24,
        minutes: Math.floor(clamped / 60000) % 60,
        seconds: Math.floor(clamped / 1000) % 60,
        isPast: remaining <= 0,
    }
}

const FirstSFGCeremony = ({onNotify}: props) => {
    const countdown = useCountdown(ceremony.start)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [name, setName] = useState('')
    const [guests, setGuests] = useState(1)
    const [confirmation, setConfirmation] = useState('')
    const [checked, setChecked] = useState<string[]>([])

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault()
        if (!name.trim()) return
        setConfirmation(`1SFG-${Math.floor(1000 + Math.random() * 9000)}`)
        setIsFormOpen(false)
    }

    return (
        <div className={'sfg-ceremony'}>
            <article className={'sfg-ticket'} aria-label={'Ceremony invitation'}>
                <div className={'sfg-ticket-main'}>
                    <p className={'sfg-ticket-kicker'}>The Commander and Command Sergeant Major request the honor of your presence</p>
                    <h3 className={'sfg-ticket-title'}>{ceremony.title}</h3>
                    <p className={'sfg-ticket-unit'}>{ceremony.unit}</p>
                    <div className={'sfg-ticket-handoff'}>
                        <span>{ceremony.outgoing}</span>
                        <span className={'sfg-ticket-arrow'} aria-label={'relinquishes to'}>→</span>
                        <span>{ceremony.incoming}</span>
                    </div>
                    <dl className={'sfg-ticket-facts'}>
                        <div><dt>Date</dt><dd>{ceremony.displayDate}</dd></div>
                        <div><dt>Time</dt><dd>{ceremony.displayTime}</dd></div>
                        <div className={'wide'}><dt>Location</dt><dd>{ceremony.location}</dd></div>
                    </dl>
                    <p className={'sfg-ticket-fine'}>{ceremony.hostedBy} · {ceremony.attire}</p>
                </div>
                <div className={'sfg-ticket-stub'} aria-hidden={true}>
                    <span className={'sfg-ticket-stub-text'}>Admit one</span>
                    <span className={'sfg-ticket-stub-code'}>{confirmation || 'RSVP REQ'}</span>
                </div>
            </article>

            <div className={'sfg-ceremony-panel'}>
                <p className={'sfg-panel-label'}>{countdown.isPast ? 'Ceremony complete' : 'T-minus'}</p>
                <div className={'sfg-countdown'} role={'timer'} aria-live={'off'}>
                    {[
                        {value: countdown.days, label: 'Days'},
                        {value: countdown.hours, label: 'Hrs'},
                        {value: countdown.minutes, label: 'Min'},
                        {value: countdown.seconds, label: 'Sec'},
                    ].map(unit => (
                        <div key={unit.label} className={'sfg-countdown-unit'}>
                            <span className={'sfg-countdown-value'}>{pad(unit.value)}</span>
                            <span className={'sfg-countdown-label'}>{unit.label}</span>
                        </div>
                    ))}
                </div>

                {confirmation ?
                    <div className={'sfg-rsvp-confirmed'} role={'status'}>
                        <p className={'sfg-panel-label'}>RSVP logged</p>
                        <p className={'sfg-rsvp-code'}>{confirmation}</p>
                        <p>{name.trim()} · party of {guests}. Show this code at the reception check-in.</p>
                        <button className={'sfg-link-btn'} onClick={() => {setConfirmation(''); setIsFormOpen(true)}}>Change RSVP</button>
                    </div>
                    : isFormOpen ?
                    <form className={'sfg-rsvp-form'} onSubmit={handleSubmit}>
                        <label className={'sfg-field'}>
                            <span>Name and rank</span>
                            <input value={name} onChange={event => setName(event.target.value)} placeholder={'SSG Jane Doe'} required autoFocus/>
                        </label>
                        <div className={'sfg-field'}>
                            <span id={'sfg-guests-label'}>Party size</span>
                            <div className={'sfg-stepper'} role={'group'} aria-labelledby={'sfg-guests-label'}>
                                <button type={'button'} onClick={() => setGuests(Math.max(1, guests - 1))} aria-label={'Fewer guests'} disabled={guests <= 1}>−</button>
                                <output aria-live={'polite'}>{guests}</output>
                                <button type={'button'} onClick={() => setGuests(Math.min(6, guests + 1))} aria-label={'More guests'} disabled={guests >= 6}>+</button>
                            </div>
                        </div>
                        <div className={'sfg-form-actions'}>
                            <button type={'submit'} className={'sfg-btn sfg-btn-primary'}>Confirm RSVP</button>
                            <button type={'button'} className={'sfg-link-btn'} onClick={() => setIsFormOpen(false)}>Cancel</button>
                        </div>
                        <p className={'sfg-fine'}>Demo only. Nothing is sent anywhere.</p>
                    </form>
                    :
                    <button className={'sfg-btn sfg-btn-primary sfg-btn-block'} onClick={() => setIsFormOpen(true)}>RSVP now</button>
                }

                <div className={'sfg-ceremony-actions'}>
                    <button className={'sfg-btn sfg-btn-ghost'} onClick={() => {downloadCalendarInvite(); onNotify('Calendar invite downloaded.')}}>
                        Add to calendar
                    </button>
                    <a className={'sfg-btn sfg-btn-ghost'} href={ceremony.mapsUrl} target={'_blank'} rel={'noreferrer'}>
                        Directions ↗
                    </a>
                </div>

                <div className={'sfg-checklist'}>
                    <p className={'sfg-panel-label'}>Before you go</p>
                    <ul>
                        {ceremony.checklist.map(item => (
                            <li key={item}>
                                <label className={'sfg-check'}>
                                    <input
                                        type={'checkbox'}
                                        checked={checked.includes(item)}
                                        onChange={() => setChecked(current => current.includes(item) ? current.filter(entry => entry !== item) : [...current, item])}
                                    />
                                    <span>{item}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    )
}
export default FirstSFGCeremony
